import { useEffect, useRef, useState } from 'react';
import { useContext } from 'react';
import { UserContext } from "../../App";
import { useParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { generateClient } from "aws-amplify/api";
import { getUrl } from "aws-amplify/storage";
import ReactPlayer from 'react-player'
import { MDBBtn, MDBCard, MDBCardBody, MDBCardImage, MDBCardTitle, MDBCol, MDBIcon, MDBRow } from "mdb-react-ui-kit";
import { parse } from "uuid";
import { getLocalizedText } from "../../utils/localizedText";
import { captureError } from '../../utils/sentryHelpers';



export default function LectureVideo() {
    const { t, i18n } = useTranslation();
    const { user } = useContext(UserContext);
    const [userAttendedEvents, setUserAttendedEvents] = useState([]);

    const { lectureId } = useParams();

    // Get lecture from API with the id
    const client = generateClient();
    const [lecture, setLecture] = useState(null);

    const getLecture = async () => {
        try {
            const res = await client.graphql({
                query: /* GraphQL */ `
                query GetLecture($id: ID!) {
                  getLecture(id: $id) {
                    id
                    type
                    startTime
                    endTime
                    dateTimes
                    event {
                      id
                      publicVideos
                      name {
                        language
                        text
                      }
                      image {
                        fileName
                      }
                      date
                      startDate
                      endDate 
                    }
                    company {
                      id
                      name
                      contact {
                        phone
                        mobile
                        fax
                        email
                      }
                      website
                      image {
                        main {
                            fileName
                        }
                      }
                      banner {
                        fileName
                      } 
                    }
                    companyBooth
                    speakers {
                      items {
                        id
                        lectureId
                        employeeId
                        createdAt
                        updatedAt
                        employee {
                            id
                            image {
                            fileName
                            }
                            user {
                                firstName
                                lastName
                                image {
                                fileName
                                }
                            }
                            }
                      }
                    }
                    speakersUsers {
                      items {
                        id
                        user {
                            id
                            firstName
                            middleName
                            lastName
                            title
                            image {
                                fileName
                                alt
                            }
                        }
                      }
                    }
                    session
                    mainSpeaker {
                      id
                      userEmployeeProfilesId
                    }
                    room
                    title {
                      language
                      text
                    }
                    subjects {
                      id
                      name {
                        language
                        text
                      }
                    }
                    topics {
                      items {
                        topic {
                            id
                            name {
                                language
                                text
                            }
                            }
                      }
                    }
                    content {
                      language
                      text
                    }
                    presentationLanguage
                    image {
                      fileName
                    }
                    pdfDe {
                      url
                      s3Path
                      fileName
                      type
                      name
                    }
                    pdfEn {
                      url
                      s3Path
                      fileName
                      type
                      name
                    }
                    videoS3Key {
                      s3Path
                    }
                    approved
                    lengthInSeconds
                    status
                    createdAt
                    updatedAt
                    companyLecturesId
                    eventLecturesId
                    lectureMainSpeakerId
                    lectureAdminTopicId
                  }
                }
              `,
                variables: { id: lectureId }
            });
            setLecture(res.data.getLecture);
            getVideoUrl(res.data.getLecture.videoS3Key.s3Path);

        }
        catch (error) {
            console.error(error);
            captureError(error, {
                feature: 'academy',
                operation: 'load-lecture',
                consoleMessage: 'Error loading lecture:',
                context: { lectureId }
            });
        }
    }

    const fetchUserEvents = async () => {
      try {
          const ticketsResponse = await client.graphql({
              query: /* GraphQL */ `
                  query GetUser($id: ID!) {
                      getUser(id: $id) {
                          id
                          owner
                          tickets {
                              items {
                                  ticketEventId
                                  event {
                                      id
                                      name {
                                          language
                                          text
                                      }
                                      status
                                      startDate
                                      endDate
                                      publicVideos
                                  }
                              }
                          }
                      }
                  }
              `,
              variables: {
                  id: user.id
              }
          });
  
          const userTickets = ticketsResponse.data.getUser.tickets.items;
          const userEvents = userTickets
              .filter(ticket => ticket.event)
              .map(ticket => ticket.event);
          
          setUserAttendedEvents(userEvents);
      } catch (error) {
          console.error("Error fetching user events:", error);
          captureError(error, {
              feature: 'academy',
              operation: 'fetch-user-events',
              consoleMessage: 'Error fetching user events:',
              context: { userId: user.id }
          });
      }
  };

    // Get video URL from S3
    const [videoUrl, setVideoUrl] = useState(null);
    const getVideoUrl = async (s3Path) => {
        try {
            const url = await getUrl({
                key: s3Path,
                level: 'public',
                options: {
                    expires: 60
                }
            });
            setVideoUrl(url.url);
        }
        catch (error) {
            console.error(error);
            captureError(error, {
                feature: 'academy',
                operation: 'get-video-url',
                consoleMessage: 'Error getting video URL:',
                context: { s3Path }
            });
        }
    }

    // Video Play progress
    // Get video progress from API and start the video player from the last time. Track the progress and update the progress in the API every 10 seconds. Track the last timestamp and the total time watched.
    // type VideoProgress
    // {
    // id: ID!
    // userId: String @index(name: "byUser", queryField: "videoProgressByUser")
    // videoId: String @index(name: "byVideo", sortKeyFields: ["userId"], queryField: "videoProgressByVideoAndUser")
    // progressInSeconds: Int
    // totalWatchedInSeconds: Int
    // }
    const playerRef = useRef(null);
    const [progressId, setProgressId] = useState(null);
    const [lastTime, setLastTime] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [totalWatched, setTotalWatched] = useState(0);

    const getProgress = async () => {
        try {
            const res = await client.graphql({
                query: /* GraphQL */ `
                query VideoProgressByVideoAndUser(
                  $videoId: String!
                  $userId: ModelStringKeyConditionInput
                ) {
                  videoProgressByVideoAndUser(
                    videoId: $videoId
                    userId: $userId
                  ) {
                    items {
                      id
                      userId
                      videoId
                      progressInSeconds
                      totalWatchedInSeconds
                      createdAt
                      updatedAt
                      __typename
                    }
                    nextToken
                    __typename
                  }
                }
              `,
                variables: { userId: {eq: user.sub}, videoId: lecture.id }
            });
            if (res.data.videoProgressByVideoAndUser.items.length > 0) {
                const progress = res.data.videoProgressByVideoAndUser.items[0];
                setProgressId(progress.id);
                setLastTime(progress.progressInSeconds);
                setCurrentTime(progress.progressInSeconds);
                setTotalWatched(progress.totalWatchedInSeconds);
                playerRef.current.seekTo(progress.progressInSeconds);
            }
        }
        catch (error) {
            console.error(error);
            captureError(error, {
                feature: 'academy',
                operation: 'get-video-progress',
                consoleMessage: 'Error getting video progress:',
                context: { lectureId: lecture.id, userId: user.sub }
            });
        }
    }

    const updateProgress = async (progress, totalWatched) => {
        console.log("Update Progress", progress, totalWatched)
        try {
            if(progressId) {
                const res = await client.graphql({
                    query: /* GraphQL */ `
                    mutation UpdateVideoProgress(
                        $input: UpdateVideoProgressInput!
                        $condition: ModelVideoProgressConditionInput
                        ) {
                        updateVideoProgress(input: $input, condition: $condition) {
                            id
                            userId
                            videoId
                            progressInSeconds
                            totalWatchedInSeconds
                            createdAt
                            updatedAt
                            __typename
                        }
                    }
                    `,
                    variables: {
                        input: {
                            id: progressId,
                            userId: user.sub,
                            videoId: lecture.id,
                            lectureVideoProgressId: lecture.id,
                            userVideoProgressId: user.id,
                            progressInSeconds: parseInt(progress),
                            totalWatchedInSeconds: parseInt(totalWatched)
                        }
                    }
                });
            } else {
                const res = await client.graphql({
                    query: /* GraphQL */ `
                    mutation CreateVideoProgress(
                        $input: CreateVideoProgressInput!
                        ) {
                        createVideoProgress(input: $input) {
                            id
                            userId
                            videoId
                            progressInSeconds
                            totalWatchedInSeconds
                            createdAt
                            updatedAt
                            __typename
                        }
                    }
                    `,
                    variables: {
                        input: {
                            userId: user.sub,
                            videoId: lecture.id,
                            progressInSeconds: parseInt(progress),
                            totalWatchedInSeconds: parseInt(totalWatched)
                        }
                    }
                });
                setProgressId(res.data.createVideoProgress.id);
            }

        }
        catch (error) {
            console.error(error);
            captureError(error, {
                feature: 'academy',
                operation: 'update-video-progress',
                consoleMessage: 'Error updating video progress:',
                context: {
                    lectureId: lecture.id,
                    userId: user.sub,
                    progressInSeconds: progress,
                    totalWatchedInSeconds: totalWatched
                }
            });
        }
    }

    const handleProgress = (state) => 
{
    setCurrentTime(state.playedSeconds);

    // Berechne die tatsächlich angeschauten Sekunden
    if (state.playedSeconds > lastTime + 5) {
        // Update lastTime und füge den Wert zur Gesamtzeit hinzu
        const newTotalWatched = totalWatched + (state.playedSeconds - lastTime);
        setTotalWatched(newTotalWatched);
        setLastTime(state.playedSeconds);
        updateProgress(state.playedSeconds, newTotalWatched);
    }
}

    useEffect(() => {
        getLecture();
    }, [lectureId]);

    useEffect(() => {
      if (lectureId && user?.id) {
          fetchUserEvents();
      }
  }, [user, lectureId]);



    const downloadFile = async (fileName, path) => {
        const regex = /protected\/(eu-central-1:[a-z0-9-]+)\//i;
        const match = path.match(regex);
        const identity = match[1];
        const getUrlResult = await getUrl({
          key: fileName,
          options: {
            accessLevel: 'protected' ,
            targetIdentityId: identity,
            expires: 60,
          },
        });
        console.log('signed URL: ', getUrlResult.url);
        window.open(getUrlResult.url, '_blank');
      }

    // const [duration, setDuration] = useState(null);

    // const updateLectureDuration = async (duration) => {
    //     try {
    //         const res = await client.graphql({
    //             query: /* GraphQL */ `
    //             mutation UpdateLecture($input: UpdateLectureInput!) {
    //               updateLecture(input: $input) {
    //                 id
    //                 lengthInSeconds
    //               }
    //             }
    //           `,
    //             variables: {
    //                 input: {
    //                     id: lecture.id,
    //                     lengthInSeconds: Math.floor(duration)
    //                 }
    //             }
    //         });
    //         console.log("Updated Lecture Duration", res.data.updateLecture.lengthInSeconds);
    //     }
    //     catch (error) {
    //         console.error(error);
    //     }
    // }

    // useEffect(() => {
    //     if(duration && !lecture.lengthInSeconds) {
    //       console.log("Update Duration", duration)
    //       updateLectureDuration(duration);
    //     }
    // }, [duration]);

    const hasDocumentAccess = (lecture) => {
      return user.hasInsightAccess || userAttendedEvents.find(e => e.id === lecture.event?.id);
  };

  return (
    lecture &&
<>

<MDBCard>
    <MDBCardBody>
        
        
       
       
        {/* <div>
          {lecture.lengthInSeconds}
          <br></br>
          {Math.floor(duration)}
        </div> */}
        <MDBRow className="mt-2 mt-md-4">
            <MDBCol md="4">
              <MDBCardImage
                src={lecture.image?.fileName ? "https://iec-cp-public.s3.eu-central-1.amazonaws.com/lectureImages/" + lecture.image.fileName : undefined}
                alt={getLocalizedText(lecture.title)}
                className='rounded mb-4'
                style={{ aspectRatio: "16 / 9" , objectFit: "cover" }}
                fluid />
                <MDBCardTitle>{lecture.title.find(t => t.language === i18n.language)?.text || lecture.title[0]?.text}</MDBCardTitle>
                <h6>{t("Subjects")}</h6>
                <ul>
                    {lecture.subjects.map(subject => (
                        <li key={subject.id}>{subject.name.find(n => n.language === i18n.language)?.text || subject.name[0]?.text}</li>
                    ))}
                </ul>
                {(lecture?.pdfDe?.fileName || lecture?.pdfEn?.fileName) && <h6>{t("Lecture documents")}</h6>}
                {
                    lecture?.pdfDe?.fileName && 
                    <div className="text-center" style={{maxWidth: "200px"}}>
                        <MDBBtn 
                            color="link" 
                            onClick={() => downloadFile(lecture.pdfDe.fileName, lecture.pdfDe.s3Path)} 
                            size="sm" 
                            className="mb-0"
                            disabled={!hasDocumentAccess(lecture)}
                        >
                            {t("Download lecture documents - German")}
                        </MDBBtn>
                    </div>
                }
                {
                    lecture?.pdfEn?.fileName &&
                    <div className="text-center" style={{maxWidth: "200px"}}>
                        <MDBBtn 
                            color="link" 
                            onClick={() => downloadFile(lecture.pdfEn.fileName, lecture.pdfEn.s3Path)} 
                            size="sm" 
                            className="mb-0"
                            disabled={!hasDocumentAccess(lecture)}
                        >
                            {t("Download lecture documents - English")}
                        </MDBBtn>
                    </div>
                }
               
            </MDBCol>
            <MDBCol md="8">
            {
            lecture.event?.publicVideos || user.hasInsightAccess ?
            <ReactPlayer
            ref={playerRef}
            url={videoUrl}
            controls={true}
            width="100%"
            height="auto"
            config={{
                file: {
                    attributes: {
                        controlsList: "nodownload",
                        onContextMenu: e => e.preventDefault()
                    }
                }
    
            }}
            onReady={(e) =>  {if(user.sub && !progressId) getProgress()}}
            // onDuration={(duration) => setDuration(duration)}
            onProgress={handleProgress}
            onSeek={e => {
                setLastTime(e)
            }}
            />
            :
            <div className="text-center">
                <MDBIcon fas icon="lock" size="3x" className="text-danger" />
                <p className="text-danger">{t("You do not have access to this video")}</p>
            </div>
        }
                <h5 className="mt-4">{t("Content")}</h5>
                <p>{getLocalizedText(lecture.content)}</p>
            </MDBCol>
        </MDBRow>
        <hr></hr>
        <MDBRow>
            <MDBCol md="6">
                <h5>{t("Speakers")}</h5>
                <ul style={{listStyleType: "none"}}>
                    {lecture.speakers.items.map(speaker => (
                        <li key={speaker.id}>
                            <div className='d-flex align-items-center text-start'>
                            {speaker.employee.image?.fileName || speaker.employee.user?.image?.fileName ?
                                        <img
                                            src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/"+ (speaker.employee.image?.fileName || speaker.employee.user?.image?.fileName)}
                                            className="rounded-circle border"
                                            style={{ height: "50px", width: "50px", objectFit: "cover" }}
                                            alt={speaker.employee.image?.alt || speaker.employee.user?.image?.alt}
                                        />
                                        :
                                        <MDBIcon className="rounded-circle border" style={{ height: "50px", width: "50px", textAlign: "center", paddingTop: "12px", fontSize: "20px" }} fas icon="user-alt" />

                            }
                            <div className='ms-3'>
                            <p className='fw-bold mb-1'>{speaker.employee.user.title ? speaker.employee.user.title +" " : ""}{speaker.employee.user?.firstName} {speaker.employee.user?.middleName} {speaker.employee.user?.lastName}</p>
                            {/* <p className='text-muted mb-0'><MDBIcon className="me-2" fas icon="comments" /> {t("Connect")}</p> */}
                            </div>
                            </div>
                        </li>
                    ))}
                    {lecture.speakersUsers?.items?.map(speakerUser => (
                        <li key={`user-${speakerUser.id}`}>
                            <div className='d-flex align-items-center text-start'>
                            {speakerUser.user?.image?.fileName ?
                                        <img
                                            src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/"+ speakerUser.user.image.fileName}
                                            className="rounded-circle border"
                                            style={{ height: "50px", width: "50px", objectFit: "cover" }}
                                            alt={speakerUser.user.image?.alt}
                                        />
                                        :
                                        <MDBIcon className="rounded-circle border" style={{ height: "50px", width: "50px", textAlign: "center", paddingTop: "12px", fontSize: "20px" }} fas icon="user-alt" />
                            }
                            <div className='ms-3'>
                            <p className='fw-bold mb-1'>{speakerUser.user?.title ? speakerUser.user.title +" " : ""}{speakerUser.user?.firstName} {speakerUser.user?.middleName} {speakerUser.user?.lastName}</p>
                            </div>
                            </div>
                        </li>
                    ))}
                </ul>
            </MDBCol>
            <MDBCol md="6">
                <h5>{t("Company")}</h5>
                <div className='d-flex align-items-center'>
                    {lecture.company?.image?.main?.fileName ?
                        <img
                            src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/"+lecture.company.image.main.fileName}
                            className=""
                            style={{ height: "50px", width: "50px", objectFit: "contain" }}
                            alt={lecture.company.image.alt}
                        />
                    :
                        <MDBIcon className="" style={{ height: "50px", width: "50px", textAlign: "center", paddingTop: "12px", fontSize: "20px" }} fas icon="company-alt" />
                                
                    }
                    <div className='ms-3'>
                      <p className='fw-bold mb-1'>{lecture.company?.name}</p>
                      <p className='text-muted mb-0'><a href={lecture.company?.website} target="_blank" rel="noreferrer">{lecture.company?.website}</a></p>
                    </div>
                    </div>
            </MDBCol>
        </MDBRow>
    </MDBCardBody>
</MDBCard>
</>
  )
}
